import { useEffect, useMemo, useState } from "react";
import { Volume2, VolumeX, Image as ImageIcon } from "lucide-react";

function buildHeroSrc(source, { muted = true } = {}) {
  if (!source) return "";
  if (source.type === "youtube") {
    const id = source.video_id;
    const params = new URLSearchParams({
      autoplay: "1", mute: muted ? "1" : "0", loop: "1", playsinline: "1",
      rel: "0", modestbranding: "1", controls: "0", iv_load_policy: "3",
      disablekb: "1", cc_load_policy: "0", playlist: id,
    });
    return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
  }
  if (source.type === "vimeo") {
    const id = source.video_id;
    const params = new URLSearchParams({
      autoplay: "1", muted: muted ? "1" : "0", loop: "1", background: "1",
      dnt: "1", title: "0", byline: "0", portrait: "0",
      playsinline: "1", pip: "1",
    });
    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
  }
  return "";
}

/**
 * Apple-TV style cinematic hero.
 *  - source: { trailer_url? , trailer_source?, hero_banner_url?, hero_image_url?, thumbnail_url?, title, description }
 *  - children: lower-left CTA stack (Play / Add / More info)
 *  - heightClass: tailwind utility for height (default `h-[100vh]`)
 *  - tintColor: theme color tinted overlay (defaults to none)
 *  - rotateMs: when items.length > 1, cross-fade through them every X ms
 *  - prefers reduced-motion → falls back to static image (no autoplay).
 */
export default function HeroAutoplay({
  items,
  heightClass = "h-[88vh] min-h-[560px]",
  themeColor = "#7C3AED",
  accentColor = "#F59E0B",
  rotateMs = 12000,
  testid = "hero-autoplay",
  children,
}) {
  const list = (items || []).filter(Boolean);
  const [muted, setMuted] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setActiveIdx((i) => (i + 1) % list.length), rotateMs);
    return () => clearInterval(t);
  }, [list.length, rotateMs]);

  if (list.length === 0) {
    return (
      <section className={`relative ${heightClass} grid place-items-center bg-[#0a0a0a] border-b border-white/5`} data-testid={`${testid}-empty`}>
        <div className="text-center text-white/40">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-xs uppercase tracking-[0.4em]">No featured title yet</div>
        </div>
      </section>
    );
  }

  const active = list[activeIdx] || list[0];

  return (
    <section className={`relative ${heightClass} overflow-hidden bg-black`} data-testid={testid}>
      {/* All sources mounted, opacity-toggled for cross-fade. Only the active one autoplays. */}
      {list.map((it, idx) => (
        <HeroLayer
          key={`${it.id || it.slug || idx}-${idx}`}
          item={it}
          isActive={idx === activeIdx}
          muted={muted}
          reducedMotion={reducedMotion}
          themeColor={themeColor}
        />
      ))}

      {/* Bottom gradient → seamless transition into the page below */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{
        backgroundImage: "linear-gradient(180deg, transparent 0%, rgba(10,10,10,0.55) 60%, #0a0a0a 100%)",
      }} />
      {/* Left wash so headline always reads */}
      <div className="absolute inset-y-0 left-0 w-1/2 pointer-events-none hidden md:block" style={{
        backgroundImage: "linear-gradient(90deg, rgba(10,10,10,0.78) 0%, transparent 100%)",
      }} />
      {/* Subtle accent tint */}
      <div className="absolute inset-0 pointer-events-none mix-blend-screen opacity-[0.07]" style={{ backgroundColor: accentColor }} />

      {/* Mute toggle, top-right of hero */}
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        className="absolute top-4 right-4 md:top-6 md:right-6 z-20 grid place-items-center w-10 h-10 border border-white/30 bg-black/40 backdrop-blur-md hover:bg-black/70 transition-colors"
        aria-label={muted ? "Unmute trailer" : "Mute trailer"}
        data-testid={`${testid}-mute-toggle`}
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* Title carousel dots */}
      {list.length > 1 && (
        <div className="absolute bottom-6 right-6 z-20 hidden md:flex items-center gap-2" data-testid={`${testid}-rotation-dots`}>
          {list.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveIdx(idx)}
              className={`h-[3px] transition-all ${idx === activeIdx ? "w-10" : "w-4 opacity-50 hover:opacity-100"}`}
              style={{ backgroundColor: idx === activeIdx ? accentColor : "white" }}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Bottom-left content slot */}
      <div className="absolute inset-x-0 bottom-0 z-10 pb-12 md:pb-20 px-6 md:px-12">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.4em] text-white/55" data-testid={`${testid}-eyebrow`}>
            Now playing on the home reel
          </div>
          <h1 className="mt-3 font-display font-black text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]" data-testid={`${testid}-title`}>
            {active.title}
          </h1>
          {active.description && (
            <p className="mt-4 text-white/75 text-sm md:text-base font-light max-w-xl line-clamp-3" data-testid={`${testid}-description`}>
              {active.description}
            </p>
          )}
          {(active.release_year || active.duration_minutes || (active.genres || [])[0]) && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/55">
              {active.release_year && <span>{active.release_year}</span>}
              {active.duration_minutes && <span>{active.duration_minutes} min</span>}
              {(active.genres || []).slice(0, 2).map((g) => (
                <span key={g} className="px-1.5 border border-white/20">{g}</span>
              ))}
            </div>
          )}
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3" data-testid={`${testid}-cta-row`}>
            {typeof children === "function" ? children(active) : children}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroLayer({ item, isActive, muted, reducedMotion, themeColor }) {
  const trailerSrc = item.trailer_source || item.video_source;
  const yt = trailerSrc?.type === "youtube" ? trailerSrc.video_id : null;
  const vimeo = trailerSrc?.type === "vimeo" ? trailerSrc.video_id : null;
  const fallbackImg = item.hero_banner_url || item.hero_image_url || item.thumbnail_url || "";
  const showVideo = isActive && !reducedMotion && (yt || vimeo);

  const iframeSrc = useMemo(() => {
    if (!showVideo) return "";
    return buildHeroSrc(trailerSrc, { muted });
  }, [showVideo, trailerSrc, muted]);

  return (
    <div
      className={`absolute inset-0 transition-opacity duration-1000 ${isActive ? "opacity-100" : "opacity-0"}`}
      aria-hidden={!isActive}
    >
      {fallbackImg ? (
        <img
          src={fallbackImg}
          alt={item.title}
          className={`absolute inset-0 w-full h-full object-cover ${reducedMotion ? "" : "scale-105 will-change-transform"}`}
          style={{ animation: reducedMotion || showVideo ? "none" : "kenBurns 18s ease-in-out infinite alternate" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${themeColor}40, #0a0a0a 60%)` }} />
      )}
      {showVideo && iframeSrc && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* 16:9 iframe scaled to cover. We delay opacity to give YT a beat to start;
             if the trailer is blocked, the image remains the dominant visual. */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] h-[100vh] min-w-full min-h-full opacity-0 transition-opacity duration-700"
               style={{ animation: "heroFadeIn 0.8s ease-out 1.4s forwards" }}>
            <iframe
              key={`${item.id}-${muted ? "m" : "u"}`}
              src={iframeSrc}
              title={item.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      )}
      <style>{`
        @keyframes kenBurns {
          0% { transform: scale(1.05) translate(0, 0); }
          100% { transform: scale(1.12) translate(-1%, 1%); }
        }
        @keyframes heroFadeIn {
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
